/**
 * Ingredient Form Page - Create/Edit Ingredient
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Package } from 'lucide-react';
import { inventoryApi } from '../lib/api/inventory';
import type { Ingredient } from '../types/inventory';
import {
  IngredientCategory,
  StorageLocation,
  AllergenType,
  CATEGORY_LABELS,
  STORAGE_LABELS,
} from '../types/inventory';

export default function IngredientFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: IngredientCategory.OTHER,
    unitOfMeasure: 'kg',
    currentStock: 0,
    minimumStock: 0,
    reorderPoint: 0,
    unitCost: 0,
    supplierName: '',
    supplierContact: '',
    storageLocation: StorageLocation.PANTRY,
    shelfLifeDays: 0,
    allergen: false,
    allergenType: undefined as AllergenType | undefined,
    isActive: true,
  });

  useEffect(() => {
    if (isEditMode) {
      loadIngredient();
    }
  }, [id]);

  const loadIngredient = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const ingredient = await inventoryApi.getIngredientById(id);
      setFormData({
        name: ingredient.name,
        description: ingredient.description || '',
        category: ingredient.category,
        unitOfMeasure: ingredient.unitOfMeasure,
        currentStock: ingredient.currentStock,
        minimumStock: ingredient.minimumStock,
        reorderPoint: ingredient.reorderPoint || 0,
        unitCost: ingredient.unitCost,
        supplierName: ingredient.supplierName || '',
        supplierContact: ingredient.supplierContact || '',
        storageLocation: ingredient.storageLocation || StorageLocation.PANTRY,
        shelfLifeDays: ingredient.shelfLifeDays || 0,
        allergen: ingredient.allergen,
        allergenType: ingredient.allergenType,
        isActive: ingredient.isActive,
      });
    } catch (error) {
      console.error('Failed to load ingredient:', error);
      alert('Errore nel caricamento dell\'ingrediente');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditMode && id) {
        await inventoryApi.updateIngredient(id, formData);
        alert('Ingrediente aggiornato con successo!');
      } else {
        await inventoryApi.createIngredient(formData);
        alert('Ingrediente creato con successo!');
      }
      navigate('/dashboard/inventory/ingredients');
    } catch (error) {
      console.error('Failed to save ingredient:', error);
      alert('Errore nel salvataggio dell\'ingrediente');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent mb-3"></div>
          <p className="text-gray-500 dark:text-white/40">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard/inventory/ingredients')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-xl transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-600 dark:text-white/60" />
        </button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? 'Modifica Ingrediente' : 'Nuovo Ingrediente'}
          </h1>
          <p className="text-gray-500 dark:text-white/40 text-sm mt-1">
            {isEditMode ? 'Aggiorna le informazioni' : 'Aggiungi un nuovo ingrediente al magazzino'}
          </p>
        </div>
      </div>

      {/* Form */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-6 space-y-6 max-w-4xl"
      >
          {/* Informazioni Base */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informazioni Base</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Nome Ingrediente *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                  placeholder="es. Pomodoro San Marzano"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">Descrizione</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                  placeholder="Descrizione opzionale dell'ingrediente"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Categoria *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value as IngredientCategory })
                  }
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                >
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Unità di Misura *
                </label>
                <select
                  required
                  value={formData.unitOfMeasure}
                  onChange={(e) => setFormData({ ...formData, unitOfMeasure: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                >
                  <option value="kg">kg (chilogrammi)</option>
                  <option value="g">g (grammi)</option>
                  <option value="L">L (litri)</option>
                  <option value="ml">ml (millilitri)</option>
                  <option value="pcs">pcs (pezzi)</option>
                  <option value="confezioni">confezioni</option>
                </select>
              </div>
            </div>
          </div>

          {/* Giacenze */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Giacenze e Scorte</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Stock Corrente *
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.currentStock}
                  onChange={(e) =>
                    setFormData({ ...formData, currentStock: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Stock Minimo *
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.minimumStock}
                  onChange={(e) =>
                    setFormData({ ...formData, minimumStock: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                />
                <p className="text-xs text-gray-500 dark:text-white/40 mt-1">Alert quando sotto questa soglia</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Punto Riordino
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.reorderPoint}
                  onChange={(e) =>
                    setFormData({ ...formData, reorderPoint: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                />
              </div>
            </div>
          </div>

          {/* Costi */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Costo</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Costo Unitario (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.unitCost}
                  onChange={(e) =>
                    setFormData({ ...formData, unitCost: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                  placeholder="0.00"
                />
              </div>
              <div className="flex items-end">
                <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl border border-emerald-200 dark:border-emerald-500/20 w-full">
                  <p className="text-sm text-gray-600 dark:text-white/60">Valore Totale</p>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {(formData.currentStock * formData.unitCost).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Fornitore */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Fornitore</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Nome Fornitore
                </label>
                <input
                  type="text"
                  value={formData.supplierName}
                  onChange={(e) => setFormData({ ...formData, supplierName: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                  placeholder="es. Ortofrutticola Mario"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Contatto Fornitore
                </label>
                <input
                  type="text"
                  value={formData.supplierContact}
                  onChange={(e) => setFormData({ ...formData, supplierContact: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                  placeholder="Email o telefono"
                />
              </div>
            </div>
          </div>

          {/* Storage & Allergeni */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Storage e Allergeni</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Locazione Storage
                </label>
                <select
                  value={formData.storageLocation}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      storageLocation: e.target.value as StorageLocation,
                    })
                  }
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                >
                  {Object.entries(STORAGE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                  Durata (giorni)
                </label>
                <input
                  type="number"
                  value={formData.shelfLifeDays}
                  onChange={(e) =>
                    setFormData({ ...formData, shelfLifeDays: parseInt(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                  placeholder="Giorni di conservazione"
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.allergen}
                    onChange={(e) => setFormData({ ...formData, allergen: e.target.checked })}
                    className="w-4 h-4 text-orange-500 border-gray-300 dark:border-white/20 dark:bg-white/[0.06] rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-white/70">
                    Questo ingrediente è un allergene
                  </span>
                </label>
              </div>

              {formData.allergen && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                    Tipo Allergene
                  </label>
                  <select
                    value={formData.allergenType || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        allergenType: e.target.value as AllergenType,
                      })
                    }
                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
                  >
                    <option value="">Seleziona tipo...</option>
                    {Object.values(AllergenType).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Active Status */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 text-orange-500 border-gray-300 dark:border-white/20 dark:bg-white/[0.06] rounded focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-white/70">Ingrediente attivo</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200 dark:border-white/[0.08]">
            <button
              type="button"
              onClick={() => navigate('/dashboard/inventory/ingredients')}
              className="px-5 py-2.5 border border-gray-300 dark:border-white/[0.08] bg-white dark:bg-white/[0.04] rounded-xl text-gray-700 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/[0.06] transition font-medium"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Salvataggio...
                </>
              ) : (
                <>
                  <Save size={18} />
                  {isEditMode ? 'Aggiorna' : 'Crea Ingrediente'}
                </>
              )}
            </button>
          </div>
        </motion.form>
    </div>
  );
}
